import { Alert, AlertTitle, Chip, Stack, Tooltip } from "@mui/material";
import { ErrorOutline as ErrorIcon } from "@mui/icons-material";
import { useState } from "react";
import type { BlockingItem } from "./mappingBlockers";

interface MappingBlockingAlertProps {
  items: BlockingItem[];
  /** Resolve an item to its navigable target locations (source-specific:
   *  spreadsheet column indices, PDF row/region indices, …), best match first. */
  resolveTargets: (item: BlockingItem) => number[];
  /** Navigate to a target location (scroll + flash). */
  onLocate: (target: number) => void;
  /** Noun for the source unit in copy — "column" (spreadsheet) / "field" (PDF). */
  sourceUnit?: string;
}

/**
 * Shared upload-blocking alert (#6118). Renders the sticky "N issues to resolve
 * before upload" banner with one clickable chip per issue; repeat clicks cycle
 * through an issue's candidate locations. The locate mechanism is injected by
 * the host so the same alert serves the spreadsheet grid (jump-to-column) and
 * the PDF surface (jump-to-page/region).
 */
export const MappingBlockingAlert = ({
  items,
  resolveTargets,
  onLocate,
  sourceUnit = "column",
}: MappingBlockingAlertProps) => {
  // Cycle index per issue — each click advances to the next candidate (wrapping).
  const [cycleIdx, setCycleIdx] = useState<Record<string, number>>({});

  if (items.length === 0) return null;

  const jumpToNext = (id: string, targets: number[]) => {
    if (targets.length === 0) return;
    const idx = cycleIdx[id] ?? 0;
    onLocate(targets[idx % targets.length]);
    setCycleIdx((prev) => ({ ...prev, [id]: idx + 1 }));
  };

  const detailFor = (item: BlockingItem, count: number): string => {
    if (item.kind === "duplicate") {
      const n = item.sourceHeaders?.length ?? count;
      return `Mapped by ${n} ${sourceUnit}s — a field can only be mapped once.${count > 0 ? " Click to cycle through them." : ""}`;
    }
    if (item.kind === "unknown-field") {
      const base = `"${item.label}" is not a valid field for this schema — remap this ${sourceUnit} to a valid field.`;
      return count > 0 ? `${base} Click to jump to it.` : base;
    }
    if (count === 0)
      return `Required field not mapped, and no ${sourceUnit} looks like a match — add or fix a source ${sourceUnit}.`;
    if (count === 1) return `Required field not mapped — jump to the ${sourceUnit} that most likely holds it.`;
    return `Required field not mapped — click to cycle through ${count} likely ${sourceUnit}s.`;
  };

  return (
    <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 2, position: "sticky", top: 0, zIndex: 3 }}>
      <AlertTitle sx={{ mb: 0.5 }}>
        {items.length} issue{items.length !== 1 ? "s" : ""} to resolve before upload
      </AlertTitle>
      <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", rowGap: 0.75 }}>
        {items.map((item) => {
          const id = `${item.kind}:${item.key}`;
          const targets = resolveTargets(item);
          const canJump = targets.length > 0;
          const noun =
            item.kind === "duplicate" ? "Duplicate" : item.kind === "unknown-field" ? "Unknown field" : "Required";
          return (
            <Tooltip key={id} title={detailFor(item, targets.length)} arrow>
              <Chip
                clickable={canJump}
                onClick={canJump ? () => jumpToNext(id, targets) : undefined}
                // No icon for duplicates — a copy-style glyph read as "copy to
                // clipboard" (Dillon's review); Required keeps the error icon.
                icon={item.kind === "duplicate" ? undefined : <ErrorIcon />}
                label={targets.length > 1 ? `${noun}: ${item.label} (${targets.length})` : `${noun}: ${item.label}`}
                color="error"
                variant="outlined"
                size="small"
              />
            </Tooltip>
          );
        })}
      </Stack>
    </Alert>
  );
};
